import { useCallback, useEffect, useMemo, useState } from "react";
import { Check, ChevronDown, ChevronRight, Search, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { friendlyErrorMessage } from "@/platform/lib/error-messages";

import { Container } from "@/platform/layout/container";
import { Button } from "@/platform/ui/button";
import { Badge } from "@/platform/ui/badge";
import { Card, CardContent } from "@/platform/ui/card";
import { Input } from "@/platform/ui/input";
import { Blob, SoftStar } from "@/platform/ui/illustrations/blobs";
import { useSession } from "@/platform/auth/use-session";
import {
  getCorpusStats,
  listAdminQuestions,
  patchQuestion,
  setQuestionStatus,
  type AdminQuestionFilters,
  type AdminQuestionPage,
  type AdminQuestionRow,
  type QuestionKind,
  type QuestionStatus,
} from "@/tenants/einstein/features/admin/qa-api";
import { cn } from "@/platform/lib/cn";

const STATUSES: Array<{ value: QuestionStatus | "all"; label: string }> = [
  { value: "all", label: "Todos" },
  { value: "draft", label: "Draft" },
  { value: "review", label: "Em revisão" },
  { value: "approved", label: "Aprovadas" },
  { value: "rejected", label: "Rejeitadas" },
];

const DISCIPLINES: string[] = [
  "all",
  "portugues",
  "matematica",
  "biologia",
  "quimica",
  "fisica",
  "ingles",
  "historia",
  "geografia",
  "unknown",
];

const KINDS: Array<{ value: QuestionKind | "all"; label: string }> = [
  { value: "all", label: "Todos kinds" },
  { value: "objective", label: "Objetivas" },
  { value: "discursive", label: "Discursivas" },
];

const YEARS = ["all", 2022, 2023, 2024, 2025, 2026] as const;

const DISCIPLINE_LABELS: Record<string, string> = {
  portugues: "Português",
  matematica: "Matemática",
  biologia: "Biologia",
  quimica: "Química",
  fisica: "Física",
  ingles: "Inglês",
  historia: "História",
  geografia: "Geografia",
  unknown: "Não classificada",
};

export function AdminQAPage() {
  const { session } = useSession();
  const [filters, setFilters] = useState<AdminQuestionFilters>({
    status: "draft",
    discipline: "all",
    kind: "all",
    year: "all",
    page: 1,
    pageSize: 20,
  });
  const [data, setData] = useState<AdminQuestionPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkRunning, setBulkRunning] = useState(false);
  const [stats, setStats] = useState<{
    byStatus: Record<string, number>;
    byDiscipline: Array<{ discipline: string; total: number; approved: number }>;
  } | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [searchInput, setSearchInput] = useState("");

  const reload = useCallback(
    async (f: AdminQuestionFilters) => {
      setLoading(true);
      try {
        const r = await listAdminQuestions(f);
        setData(r);
      } catch (err) {
        toast.error(friendlyErrorMessage(err, "Falha ao carregar."));
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    void reload(filters);
    setSelectedIds(new Set());
  }, [filters, reload]);

  useEffect(() => {
    getCorpusStats().then(setStats).catch(() => {});
  }, []);

  function updateFilter<K extends keyof AdminQuestionFilters>(k: K, v: AdminQuestionFilters[K]) {
    setFilters((prev) => ({ ...prev, [k]: v, page: 1 }));
  }

  function toggleSelected(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function selectAllVisible() {
    if (!data) return;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const r of data.rows) next.add(r.id);
      return next;
    });
  }

  function clearSelection() {
    setSelectedIds(new Set());
  }

  async function bulkSetStatus(status: QuestionStatus) {
    if (selectedIds.size === 0) return;
    const ids = Array.from(selectedIds);
    setBulkRunning(true);
    const toastId = toast.loading(
      `Atualizando ${ids.length} questão${ids.length > 1 ? "s" : ""}…`,
    );
    try {
      // Serializa em chunks de 10 para não estourar conexões.
      const chunkSize = 10;
      let done = 0;
      for (let i = 0; i < ids.length; i += chunkSize) {
        const chunk = ids.slice(i, i + chunkSize);
        await Promise.all(
          chunk.map((id) => setQuestionStatus(id, status, session?.user.id)),
        );
        done += chunk.length;
      }
      toast.success(
        `${done} questão${done > 1 ? "s" : ""} ${status === "approved" ? "aprovada(s)" : "rejeitada(s)"}.`,
        { id: toastId },
      );
      setSelectedIds(new Set());
      void reload(filters);
      getCorpusStats().then(setStats).catch(() => {});
    } catch (err) {
      toast.error(friendlyErrorMessage(err, "Falha ao aplicar em lote."), { id: toastId });
    } finally {
      setBulkRunning(false);
    }
  }

  async function handleAction(row: AdminQuestionRow, status: QuestionStatus) {
    try {
      await setQuestionStatus(row.id, status, session?.user.id);
      toast.success(`Questão ${status === "approved" ? "aprovada" : "rejeitada"}.`);
      // remove da lista atual sem refetch completo
      setData((d) =>
        d ? { ...d, rows: d.rows.filter((r) => r.id !== row.id) } : d,
      );
      getCorpusStats().then(setStats).catch(() => {});
    } catch (err) {
      toast.error(friendlyErrorMessage(err, "Falha."));
    }
  }

  function toggleExpanded(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const totalPages = useMemo(() => {
    if (!data) return 1;
    return Math.max(1, Math.ceil(data.total / data.pageSize));
  }, [data]);

  const STATUS_TONE: Record<string, "accent" | "pink" | "mint" | "yellow" | "coral"> = {
    draft: "accent",
    review: "yellow",
    approved: "mint",
    rejected: "pink",
  };

  return (
    <div className="relative overflow-hidden bg-mesh">
      <Blob
        color="violet"
        className="pointer-events-none absolute -left-24 -top-16 w-96 opacity-30 blur-2xl animate-blob"
      />
      <Blob
        color="pink"
        className="pointer-events-none absolute -right-32 top-80 w-96 opacity-25 blur-2xl animate-blob"
      />
      <SoftStar
        color="yellow"
        className="pointer-events-none absolute right-10 top-20 w-12 opacity-60 animate-float"
      />

      <Container size="wide" className="relative py-14 md:py-20">
        <header className="animate-fade-up">
          <Badge variant="accentSolid">
            <Sparkles className="size-3" aria-hidden />
            Admin · curadoria
          </Badge>
          <h1 className="mt-4 font-display text-[length:var(--text-display-md)] font-semibold leading-[var(--text-display-md--line-height)] tracking-[var(--text-display-md--letter-spacing)] text-balance">
            Painel de <span className="text-gradient-accent">QA pedagógico.</span>
          </h1>
          <p className="mt-2 text-[length:var(--text-body)] text-[color:var(--color-muted-foreground)]">
            Revise, edite e promova questões. Apenas você (admin) vê esta área.
          </p>
        </header>

        {stats ? (
          <div className="mt-8 grid gap-3 md:grid-cols-4">
            {STATUSES.filter((s) => s.value !== "all").map((s) => (
              <div
                key={s.value}
                className="lift rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-5 shadow-[var(--shadow-sm)]"
              >
                <Badge variant={STATUS_TONE[s.value as string] ?? "outline"}>{s.label}</Badge>
                <p
                  data-tabular="true"
                  className="mt-3 font-display text-[length:var(--text-h1)] font-bold leading-none tracking-[var(--text-h1--letter-spacing)]"
                >
                  {stats.byStatus[s.value as string] ?? 0}
                </p>
              </div>
            ))}
          </div>
        ) : null}

      <Card className="mt-8 p-5">
        <CardContent className="p-0">
          <div className="flex flex-wrap items-center gap-2">
            {STATUSES.map((s) => (
              <Pill
                key={s.value}
                active={filters.status === s.value}
                onClick={() => updateFilter("status", s.value as QuestionStatus | "all")}
              >
                {s.label}
              </Pill>
            ))}
            <span className="mx-2 h-6 w-px bg-[color:var(--color-border)]" aria-hidden />
            <select
              value={filters.discipline ?? "all"}
              onChange={(e) => updateFilter("discipline", e.target.value as string)}
              className="rounded-full border border-[--color-border] bg-[color:var(--color-card)] px-4 py-2 text-[length:var(--text-body-sm)] focus:border-[--color-accent] focus:outline-none focus:ring-4 focus:ring-[--color-ring]"
            >
              {DISCIPLINES.map((d) => (
                <option key={d} value={d}>
                  {d === "all" ? "Todas disciplinas" : DISCIPLINE_LABELS[d] ?? d}
                </option>
              ))}
            </select>
            <select
              value={filters.kind ?? "all"}
              onChange={(e) => updateFilter("kind", e.target.value as QuestionKind | "all")}
              className="rounded-full border border-[--color-border] bg-[color:var(--color-card)] px-4 py-2 text-[length:var(--text-body-sm)] focus:border-[--color-accent] focus:outline-none focus:ring-4 focus:ring-[--color-ring]"
            >
              {KINDS.map((k) => (
                <option key={k.value} value={k.value}>
                  {k.label}
                </option>
              ))}
            </select>
            <select
              value={String(filters.year ?? "all")}
              onChange={(e) =>
                updateFilter(
                  "year",
                  e.target.value === "all" ? "all" : (Number(e.target.value) as number),
                )
              }
              className="rounded-full border border-[--color-border] bg-[color:var(--color-card)] px-4 py-2 text-[length:var(--text-body-sm)] focus:border-[--color-accent] focus:outline-none focus:ring-4 focus:ring-[--color-ring]"
            >
              {YEARS.map((y) => (
                <option key={String(y)} value={String(y)}>
                  {y === "all" ? "Todos anos" : y}
                </option>
              ))}
            </select>
            <div className="ml-auto flex items-center gap-2">
              <div className="flex items-center gap-2 rounded-full border border-[--color-border] bg-[color:var(--color-card)] px-3">
                <Search className="size-3.5 text-[color:var(--color-muted-foreground)]" aria-hidden />
                <Input
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") updateFilter("search", searchInput);
                  }}
                  placeholder="Buscar no enunciado"
                  className="h-9 w-60 border-0 bg-transparent px-0 text-[length:var(--text-body-sm)] focus-visible:border-0 focus-visible:ring-0"
                />
              </div>
              <Button size="md" onClick={() => updateFilter("search", searchInput)}>
                Buscar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <p className="mt-8 text-[length:var(--text-body)] text-[color:var(--color-muted-foreground)]">Carregando…</p>
      ) : !data || data.rows.length === 0 ? (
        <div className="mt-8 rounded-2xl border border-dashed border-[--color-border] bg-[color:var(--color-paper)] p-10 text-center">
          <p className="text-[length:var(--text-body)] text-[color:var(--color-muted-foreground)]">
            Nada por aqui com esses filtros.
          </p>
        </div>
      ) : (
        <>
          {/* Barra de ações em massa */}
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[--color-border] bg-[color:var(--color-card)] p-3 text-[length:var(--text-body-sm)]">
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={
                    data.rows.length > 0 && data.rows.every((r) => selectedIds.has(r.id))
                  }
                  onChange={(e) => (e.target.checked ? selectAllVisible() : clearSelection())}
                  className="size-4 cursor-pointer accent-[var(--color-accent)]"
                />
                {selectedIds.size > 0
                  ? `${selectedIds.size} selecionada${selectedIds.size > 1 ? "s" : ""}`
                  : "Selecionar todas desta página"}
              </label>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                size="md"
                variant="primary"
                onClick={() => bulkSetStatus("approved")}
                disabled={selectedIds.size === 0 || bulkRunning}
                loading={bulkRunning}
              >
                Aprovar selecionadas
              </Button>
              <Button
                size="md"
                variant="outline"
                onClick={() => bulkSetStatus("rejected")}
                disabled={selectedIds.size === 0 || bulkRunning}
              >
                Rejeitar selecionadas
              </Button>
              {selectedIds.size > 0 ? (
                <Button size="md" variant="ghost" onClick={clearSelection}>
                  Limpar
                </Button>
              ) : null}
            </div>
          </div>

          <div className="mt-4 space-y-3">
            {data.rows.map((row) => (
              <QuestionRowCard
                key={row.id}
                row={row}
                expanded={expanded.has(row.id)}
                selected={selectedIds.has(row.id)}
                onSelect={() => toggleSelected(row.id)}
                onToggle={() => toggleExpanded(row.id)}
                onApprove={() => handleAction(row, "approved")}
                onReject={() => handleAction(row, "rejected")}
                onSave={async (patch) => {
                  try {
                    await patchQuestion(row.id, patch);
                    toast.success("Questão atualizada.");
                    void reload(filters);
                  } catch (err) {
                    toast.error(friendlyErrorMessage(err, "Não foi possível salvar a questão."));
                    throw err; // mantém o modal aberto preservando edição
                  }
                }}
              />
            ))}
          </div>

          <Pagination
            page={filters.page ?? 1}
            totalPages={totalPages}
            total={data.total}
            onChange={(p) => setFilters((prev) => ({ ...prev, page: p }))}
          />
        </>
      )}
      </Container>
    </div>
  );
}

function Pill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-full border-2 px-4 py-1.5 text-[length:var(--text-body-sm)] font-medium transition-colors duration-150 ease-out active:scale-[0.97]",
        active
          ? "border-[--color-accent] bg-[color:var(--color-accent)] text-white shadow-[var(--shadow-accent)]"
          : "border-[--color-border] bg-[color:var(--color-card)] text-[color:var(--color-muted-foreground)] hover:border-[--color-accent]/40 hover:text-[color:var(--color-foreground)]",
      )}
    >
      {children}
    </button>
  );
}

function statusBadgeVariant(status: QuestionStatus): "outline" | "danger" {
  return status === "rejected" ? "danger" : "outline";
}

function QuestionRowCard({
  row,
  expanded,
  selected,
  onSelect,
  onToggle,
  onApprove,
  onReject,
  onSave,
}: {
  row: AdminQuestionRow;
  expanded: boolean;
  selected: boolean;
  onSelect: () => void;
  onToggle: () => void;
  onApprove: () => void;
  onReject: () => void;
  onSave: (patch: Partial<{ stem: string; correct_answer: string; discipline: string; topic: string }>) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [stem, setStem] = useState(row.stem);
  const [correct, setCorrect] = useState(row.correct_answer ?? "");
  const [discipline, setDiscipline] = useState(row.discipline);
  const [topic, setTopic] = useState(row.topic ?? "");

  async function handleSave() {
    try {
      await onSave({
        stem: stem.trim(),
        correct_answer: correct.trim() || undefined,
        discipline,
        topic: topic.trim() || undefined,
      } as any);
      setEditing(false);
    } catch {
      // Erro já foi tratado dentro de onSave (toast.error); mantém form aberto.
    }
  }

  return (
    <Card
      className={cn(
        "overflow-hidden transition-colors",
        selected ? "ring-2 ring-[color:var(--color-accent)]/40" : "",
      )}
    >
      <div
        className="flex items-start gap-3 p-5 cursor-pointer transition-colors hover:bg-[color:var(--color-paper)]"
        onClick={onToggle}
      >
        <input
          type="checkbox"
          checked={selected}
          onChange={onSelect}
          onClick={(e) => e.stopPropagation()}
          aria-label="Selecionar para ação em lote"
          className="mt-1.5 size-4 cursor-pointer accent-[var(--color-accent)] shrink-0"
        />
        <button
          type="button"
          aria-label={expanded ? "Recolher" : "Expandir"}
          className="mt-0.5 grid size-7 place-items-center rounded-full bg-[color:var(--color-muted)] text-[color:var(--color-muted-foreground)] transition-colors hover:bg-[color:var(--color-accent-soft)] hover:text-[color:var(--color-accent)]"
        >
          {expanded ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
        </button>
        <div className="flex flex-1 flex-col gap-1.5">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={statusBadgeVariant(row.status)}>{row.status}</Badge>
            <Badge variant="accent">{row.kind}</Badge>
            <Badge variant="outline">{DISCIPLINE_LABELS[row.discipline] ?? row.discipline}</Badge>
            {row.topic ? <Badge variant="mint">{row.topic}</Badge> : null}
            <span className="text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
              <span data-tabular="true" className="font-mono">Q{row.number ?? "?"}</span> · {row.exam_year} · {row.exam_paper}
            </span>
          </div>
          <p className="text-[length:var(--text-body-sm)] text-[color:var(--color-foreground)] line-clamp-2">
            {row.stem}
          </p>
        </div>
        <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            onClick={onApprove}
            aria-label="Aprovar"
            className="grid size-9 place-items-center rounded-full bg-[color:var(--color-mint-soft)] text-[color:var(--color-mint-deep)] transition-all hover:bg-[color:var(--color-mint)] hover:text-white active:scale-[0.95]"
          >
            <Check className="size-4" aria-hidden />
          </button>
          <button
            type="button"
            onClick={onReject}
            aria-label="Rejeitar"
            className="grid size-9 place-items-center rounded-full bg-[color:var(--color-pink-soft)] text-[color:var(--color-pink-deep)] transition-all hover:bg-[color:var(--color-pink)] hover:text-white active:scale-[0.95]"
          >
            <X className="size-4" aria-hidden />
          </button>
        </div>
      </div>

      {expanded ? (
        <div className="border-t border-[--color-border] p-4 space-y-4">
          {row.has_base_text && row.base_text ? (
            <details>
              <summary className="cursor-pointer text-[length:var(--text-body-sm)] font-medium">
                Texto-base
              </summary>
              <p className="mt-2 whitespace-pre-wrap text-[length:var(--text-body-sm)] text-[color:var(--color-muted-foreground)]">
                {row.base_text}
              </p>
            </details>
          ) : null}

          {!editing ? (
            <>
              <p className="whitespace-pre-wrap text-[length:var(--text-body)] leading-relaxed">{row.stem}</p>
              {row.alternatives ? (
                <ul className="space-y-2">
                  {row.alternatives.map((a) => (
                    <li
                      key={a.label}
                      className={cn(
                        "flex items-start gap-3 rounded-lg border-2 p-3 text-[length:var(--text-body-sm)]",
                        row.correct_answer === a.label
                          ? "border-[--color-mint] bg-[color:var(--color-mint-soft)]"
                          : "border-[--color-border]",
                      )}
                    >
                      <span
                        className={cn(
                          "mt-0.5 grid size-6 place-items-center rounded-full text-[length:var(--text-caption)] font-bold shrink-0",
                          row.correct_answer === a.label
                            ? "bg-[color:var(--color-mint)] text-white"
                            : "bg-[color:var(--color-muted)] text-[color:var(--color-muted-foreground)]",
                        )}
                      >
                        {a.label}
                      </span>
                      <span className="flex-1">{a.text}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
                  Editar
                </Button>
              </div>
            </>
          ) : (
            <div className="space-y-3">
              <textarea
                value={stem}
                onChange={(e) => setStem(e.target.value)}
                rows={6}
                className="w-full rounded-sm border border-[--color-border] bg-[color:var(--color-card)] p-3 text-[length:var(--text-body-sm)]"
              />
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
                    Gabarito (A-E)
                  </label>
                  <Input
                    value={correct}
                    onChange={(e) => setCorrect(e.target.value.toUpperCase())}
                    maxLength={1}
                  />
                </div>
                <div>
                  <label className="text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
                    Disciplina
                  </label>
                  <select
                    value={discipline}
                    onChange={(e) => setDiscipline(e.target.value)}
                    className="w-full rounded-sm border border-[--color-border] bg-[color:var(--color-card)] px-3 py-2 text-[length:var(--text-body-sm)]"
                  >
                    {DISCIPLINES.filter((d) => d !== "all").map((d) => (
                      <option key={d} value={d}>
                        {DISCIPLINE_LABELS[d] ?? d}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
                    Tópico (slug)
                  </label>
                  <Input value={topic} onChange={(e) => setTopic(e.target.value)} />
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSave}>
                  Salvar
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
                  Cancelar
                </Button>
              </div>
            </div>
          )}
        </div>
      ) : null}
    </Card>
  );
}

function Pagination({
  page,
  totalPages,
  total,
  onChange,
}: {
  page: number;
  totalPages: number;
  total: number;
  onChange: (p: number) => void;
}) {
  return (
    <div className="mt-6 flex items-center justify-between">
      <p className="text-[length:var(--text-caption)] text-[color:var(--color-muted-foreground)]">
        {total} questão{total === 1 ? "" : "s"} · página {page} de {totalPages}
      </p>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => onChange(page - 1)}>
          Anterior
        </Button>
        <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => onChange(page + 1)}>
          Próxima
        </Button>
      </div>
    </div>
  );
}
